// src/components/ParticipantList.tsx
import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Users } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

interface ParticipantListProps {
  decisionId: string;
  totalCount: number;
  maxDisplayed?: number;
}

// Enhanced participant interface with auth type
interface ParticipantData {
  id: string;
  name: string;
  auth?: {
    type: 'anonymous' | 'google';
    email?: string;
  };
}

export function ParticipantList({ decisionId, totalCount, maxDisplayed = 6 }: ParticipantListProps) {
  const [participants, setParticipants] = useState<ParticipantData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allParticipants, setAllParticipants] = useState<ParticipantData[]>([]);
  const [showingAll, setShowingAll] = useState(false);
  
  useEffect(() => {
    const fetchParticipants = async () => {
      if (!decisionId) return;
      
      try {
        setLoading(true);
        const participantsRef = collection(db, `decisions/${decisionId}/participants`);
        const participantsQuery = query(
          participantsRef,
          where('status', 'in', ['active', 'completed']),
          orderBy('lastActive', 'desc'),
          limit(maxDisplayed)
        );
        
        const snapshot = await getDocs(participantsQuery);
        const participantData = snapshot.docs.map(doc => ({
          id: doc.id,
          // Use displayName if available, fall back to name
          name: doc.data().displayName || doc.data().name,
          auth: doc.data().auth
        }));
        
        setParticipants(participantData);
      } catch (err) {
        console.error('Error fetching participants:', err);
        setError('Failed to load participants');
      } finally {
        setLoading(false);
      }
    };
    
    fetchParticipants();
  }, [decisionId, maxDisplayed]);
  
  // Function to fetch all participants when needed
  const fetchAllParticipants = async () => {
    if (!decisionId || showingAll) return;
    
    try {
      setShowingAll(true);
      
      // Don't fetch if we already have all participants
      if (totalCount <= participants.length) {
        setAllParticipants(participants);
        return;
      }
      
      // Limit to 100 max to avoid performance issues with very large groups
      const maxToFetch = Math.min(totalCount, 100);
      
      const participantsRef = collection(db, `decisions/${decisionId}/participants`);
      const participantsQuery = query(
        participantsRef,
        where('status', 'in', ['active', 'completed']),
        orderBy('lastActive', 'desc'),
        limit(maxToFetch)
      );
      
      const snapshot = await getDocs(participantsQuery);
      const participantData = snapshot.docs.map(doc => ({
        id: doc.id,
        // Fix: Use displayName here too, to be consistent with the first query
        name: doc.data().displayName || doc.data().name,
        auth: doc.data().auth
      }));
      
      setAllParticipants(participantData);
    } catch (err) {
      console.error('Error fetching all participants:', err);
    }
  };
  
  if (loading) {
    return (
      <span className="flex items-center gap-2">
        <Users className="h-4 w-4" />
        <span>{totalCount} {totalCount !== 1 ? 'participants' : 'participant'}</span>
      </span>
    );
  }
  
  if (error || participants.length === 0) {
    return (
      <span className="flex items-center gap-2">
        <Users className="h-4 w-4" />
        <span>{totalCount} {totalCount !== 1 ? 'participants' : 'participant'}</span>
      </span>
    );
  }
  
  // Show all names if under limit
  if (totalCount <= maxDisplayed) {
    return (
      <span className="flex items-center gap-2">
        <Users className="h-4 w-4" />
        <span>
          {participants.map((p, i) => (
            <React.Fragment key={p.id}>
              <span className="font-medium">{p.name}</span>
              {i < participants.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}
        </span>
      </span>
    );
  }
  
  // Show limited names with dropdown to see more
  return (
    <span className="flex items-center gap-2">
      <Users className="h-4 w-4" />
      <DropdownMenu onOpenChange={(open: boolean) => {
        if (open) fetchAllParticipants();
      }}>
        <DropdownMenuTrigger className="text-left">
          <span>
            {participants.slice(0, 6).map((p, i) => (
              <React.Fragment key={p.id}>
                <span className="font-medium">{p.name}</span>
                {i < Math.min(participants.length, 6) - 1 ? ', ' : ''}
              </React.Fragment>
            ))}
            <span className="text-muted-foreground cursor-pointer hover:underline"> +{totalCount - 6} more</span>
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="start" 
          className="w-64 max-h-60 overflow-y-auto"
        >
          <div className="px-2 py-1.5 text-sm font-medium">
            All Participants ({totalCount})
          </div>
          {showingAll ? (
            allParticipants.length > 0 ? (
              allParticipants.map(p => (
                <DropdownMenuItem key={p.id} className="flex justify-between text-sm">
                  {p.name}
                </DropdownMenuItem>
              ))
            ) : (
              <div className="px-2 py-2 text-sm text-muted-foreground">Loading...</div>
            )
          ) : (
            <div className="px-2 py-2 text-sm text-muted-foreground">Click to load participants</div>
          )}
          {totalCount > 100 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground border-t mt-1">
              Showing first 100 participants
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}